import { mixed, number, object, string } from 'yup';

import { validateIpV4Address } from '@utils/validation';

import { TIdName, TValueLabelName } from '@interfaces/common/common';
import { INetworkElementCheckIpAddressDTO } from '@interfaces/networkElements/dto/networkElements';

import { getMaxLengthErrMsg, getMinLengthErrMsg, mandatoryErrMessage } from '@constants/validationConstants';
import {
	MAX_2_LENGTH,
	MAX_50_LENGTH,
	MAX_150_LENGTH,
	MAX_256_LENGTH,
	MAX_PORT_VALUE,
} from '@constants/validationMessages';
import { NETWORK_ELEMENT_TYPES } from '../constants';

export const networkElementSchema = ({
	handleCheckName,
	handleCheckIpAddress,
	id,
}: {
	handleCheckName: (dto: TIdName) => void;
	handleCheckIpAddress: (dto: INetworkElementCheckIpAddressDTO) => void;
	id: string;
}) => object({
	name: string()
		.trim()
		.max(MAX_50_LENGTH, getMaxLengthErrMsg('Name', MAX_50_LENGTH))
		.required(mandatoryErrMessage('Name'))
		.test('name', 'Name must be unique',
			async (value) => {
				if (!value) return false;

				try {
					await handleCheckName({ name: value, id });

					return true;
				} catch (err) {
					return false;
				}
			},
		),
	ipAddress: string()
		.trim()
		.required(mandatoryErrMessage('IP Address'))
		.test('ipAddress', 'Invalid IP address', validateIpV4Address)
		.test('name', async (value, context) => {
			if (!value) return false;

			try {
				await handleCheckIpAddress({ ipAddress: value, id });

				return true;
			} catch (err: any) {
				if (err?.errors?.length && err.errors[0].message) {
					return context.createError({ message: err.errors[0].message });
				}

				return context.createError({ message: 'Invalid IP address' });
			}
		}),
	userName: string()
		.trim()
		.min(MAX_2_LENGTH, getMinLengthErrMsg('Username', MAX_2_LENGTH))
		.max(MAX_150_LENGTH, getMaxLengthErrMsg('Username', MAX_150_LENGTH))
		.required(mandatoryErrMessage('Username')),
	password: string()
		.trim()
		.default('')
		.required(mandatoryErrMessage('Password')),
	netconfPort: number()
		.transform((value) => Number.isNaN(value) ? null : value)
		.min(0, 'Netconf Port must be at least 0')
		.max(MAX_PORT_VALUE, `Netconf Port must be at most ${MAX_PORT_VALUE}`)
		.nullable()
		.required(mandatoryErrMessage('Netconf Port')),
	middlewareId: object()
		.nullable()
		.required(mandatoryErrMessage('Middleware')),
	type: object()
		.nullable()
		.required(mandatoryErrMessage('Network Element Type')),
	pathDu: mixed().when(['directSftp', 'type'], {
		is: (directSftp: boolean, type: TValueLabelName) => {
			const isCorrectType = type?.value === NETWORK_ELEMENT_TYPES.CUDU
				|| type?.value === NETWORK_ELEMENT_TYPES.DU;

			return !!directSftp && isCorrectType;
		},
		then: string()
			.max(MAX_256_LENGTH, getMaxLengthErrMsg('SFTP Server Path (DU)', MAX_256_LENGTH))
			.nullable()
			.required(mandatoryErrMessage('SFTP Server Path (DU)')),
		otherwise: string().nullable().notRequired(),
	}),
	pathCu: mixed().when(['directSftp', 'type'], {
		is: (directSftp: boolean, type: TValueLabelName) => {
			const isCorrectType = type?.value === NETWORK_ELEMENT_TYPES.CUDU
				|| type?.value === NETWORK_ELEMENT_TYPES.CU;

			return !!directSftp && isCorrectType;
		},
		then: string()
			.max(MAX_256_LENGTH, getMaxLengthErrMsg('SFTP Server Path (CU)', MAX_256_LENGTH))
			.nullable()
			.required(mandatoryErrMessage('SFTP Server Path (CU)')),
		otherwise: string().nullable().notRequired(),
	}),
	lastVesTimePeriod: mixed().when(['autoSwitch'], {
		is: (autoSwitch: boolean) => !!autoSwitch,
		then: object()
			.nullable()
			.required(mandatoryErrMessage('Interval')),
		otherwise: object().nullable().notRequired(),
	}),
	netconfCheckTimePeriod: mixed().when(['autoSwitch'], {
		is: (autoSwitch: boolean) => !!autoSwitch,
		then: object()
			.nullable()
			.required(mandatoryErrMessage('Period')),
		otherwise: object().nullable().notRequired(),
	}),
});
